/* eslint-disable simple-import-sort/imports */
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
//#!if ENV === "production" && iOS !== true
// This entry will use a web-worker, which creates additional files when we
// build that must be included.
import { Document as ProdDocument, Page as ProdPage } from "react-pdf/dist/entry.webpack";
//#!else
// Note: this include uses react-pdf directly without any web-worker
// import { Document as DevDocument, Page as DevPage } from "react-pdf";
//#!endif
import PropagateLoader from "react-spinners/PropagateLoader";

import { PropTypeShapes } from "../../types";
import PrintPreviewPdfNavigation from "./PrintPreviewPdfNavigation";
import { PRVIEWABLE_SCALE_SIZES_MAP } from "../../constants/printing/printControl";
import { isNumber } from "lodash";

let Document = null;
let Page = null;
//#!if ENV === "production" && iOS !== true
Document = ProdDocument;
Page = ProdPage;
//#!else
// Document = DevDocument;
// Page = DevPage;
//#!endif

const toFileData = base64Data => (base64Data ? `data:application/pdf;base64,${base64Data}` : null);

const getPreviewableScale = pageInfo => {
  let scale = 1;
  const { pageWidth, pageHeight } = pageInfo;
  const totalSize = pageWidth * pageHeight;
  if (isNumber(totalSize) && !isNaN(totalSize)) {
    const foundSize = Object.keys(PRVIEWABLE_SCALE_SIZES_MAP).find(k => totalSize < k);
    if (foundSize) {
      scale = PRVIEWABLE_SCALE_SIZES_MAP[foundSize] / 100;
    } else {
      scale = 0.01;
    }
  }
  return scale;
};

class PrintPreviewPdfViewer extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      numPages: null,
      pageNumber: 1,
      fileData: toFileData(props.base64Data)
    };

    this._pageRenderTimer = null;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.base64Data !== this.props.base64Data) {
      this.setState({
        fileData: toFileData(nextProps.base64Data)
      });
    }
  }

  onDocumentLoadSuccess = ({ numPages }) => {
    if (numPages !== this.state.numPages) {
      this.setState({ numPages, pageNumber: Math.min(this.state.pageNumber, numPages) });
    }
  };

  goToPrevPage = () => this.setState({ pageNumber: Math.max(1, this.state.pageNumber - 1) });

  goToNextPage = () => this.setState({ pageNumber: Math.min(this.state.numPages, this.state.pageNumber + 1) });

  renderPdf() {
    const { pageNumber } = this.state;
    const loadingSpinner = <PropagateLoader size={15} color={"#ffffff"} />;
    const { errorMessage } = this.props;
    const previewableScale = getPreviewableScale(this.props.pageInfo);

    // To improve the performance for the preview, we did:
    // 1. Turn off the renderTextLayer and renderAnnotationLayer. That
    //    shows the text properly but does not render the layer for PDF editing
    //    which we do not need in preview.
    // 2. Reduce the scale according to the total size of the source data.
    //    That will render a smaller picture for preview to reduce the rendering
    //    time and avoid error/crash.
    // See https://aeontimeline.atlassian.net/browse/ATR-2864
    return (
      <Document
        file={this.state.fileData}
        onLoadSuccess={this.onDocumentLoadSuccess}
        noData={errorMessage ? errorMessage : loadingSpinner}
        loading={loadingSpinner}
      >
        <Page
          pageNumber={pageNumber}
          width={this.props.previewSize.width}
          noData={loadingSpinner}
          loading={loadingSpinner}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          scale={previewableScale}
        />
      </Document>
    );
  }

  render() {
    const { pageNumber, numPages } = this.state;
    const { width, height } = this.props.previewSize;

    const style = {
      width: `${this.props.containerSize.width}px`,
      height: `${this.props.containerSize.height}px`
    };

    const pdfWrapClass = `printPreviewPdfWrap ${width > height ? "landscape" : "portrait"}`;

    const pdfComponent = this.renderPdf();

    return (
      <div className={`printPreviewPdfViewer ${this.props.showFullSize ? "fullSize" : ""}`} style={style}>
        {this.props.showFullSize ? pdfComponent : <div className={pdfWrapClass}>{pdfComponent}</div>}
        {this.props.showNavigation ? (
          <PrintPreviewPdfNavigation
            pageNumber={pageNumber}
            numPages={numPages}
            onNextClick={this.goToNextPage}
            onPrevClick={this.goToPrevPage}
          />
        ) : null}
      </div>
    );
  }
}

PrintPreviewPdfViewer.defaultProps = {
  showNavigation: true
};

PrintPreviewPdfViewer.propTypes = {
  base64Data: PropTypes.string,
  pageInfo: PropTypes.object.isRequired,
  previewSize: PropTypeShapes.size.isRequired,
  containerSize: PropTypeShapes.size.isRequired,
  showFullSize: PropTypes.bool,
  errorMessage: PropTypes.string,
  showNavigation: PropTypes.bool
};

export default PrintPreviewPdfViewer;
